import { logger } from 'utils/system/logger'
import { VideoPlayerStore } from 'components/video/player/store'

interface IConnatixScriptService {
    loadConnatixScript: () => Promise<boolean>
}

/**
 * Loads the Connatix script if it is not already loaded.
 *
 * This method checks if the Connatix script is already loaded by inspecting
 * the `window.cnxel` object and the `connatixLoaded` property. If the script is
 * not loaded, it creates the necessary `window.cnxel` object, appends the Connatix
 * script to the document, and sets the `connatixLoaded` property to true. It also
 * updates the `VideoPlayerStore` to indicate that the script has been loaded.
 *
 * @returns {Promise<boolean>} A promise that resolves to a boolean indicating whether
 * the Connatix script is loaded.
 */
export class ConnatixScriptService implements IConnatixScriptService {
    private connatixLoaded = false
    private videoPlayerStore = VideoPlayerStore.getInstance()

    public async loadConnatixScript(): Promise<boolean> {
        /**
         * The provided Connatix code snippet dynamically creates and loads an external script within an iframe.
         * This approach is often used to isolate the script execution environment and avoid conflicts with other scripts on the main page.
         * This code ensures that the external script is loaded and executed within the isolated environment of the iframe,
         * preventing any potential conflicts with other scripts on the main page.
         */
        if (!window.cnxel && !this.connatixLoaded) {
            window.cnxel = {}
            window.cnxel.cmd = []

            const iframe = document.createElement('iframe')
            iframe.src = 'javascript:false'
            iframe.style.display = 'none'

            iframe.onload = () => {
                const iframeDoc = iframe.contentWindow?.document
                if (iframeDoc) {
                    const script = iframeDoc.createElement('script')
                    script.src =
                        '//cd.thecontentserver.com/player.js?cid=47233f53-89b8-4723-b9eb-5cff41c16bc1'
                    script.setAttribute('async', '1')
                    script.setAttribute('type', 'text/javascript')
                    iframeDoc.body.appendChild(script)
                }
            }

            document.head.appendChild(iframe)
            this.connatixLoaded = true
            this.videoPlayerStore.setScriptLoaded()
        } else if (window.cnxel && this.videoPlayerStore.isScriptLoaded) {
            this.connatixLoaded = true
            logger('info', 'Connatix API script already loaded.')
        }
        return this.connatixLoaded
    }
}
